import { SyncManager, type SyncStatus } from './sync.js'
import {
  mergeBlocks,
  mergeSpaceSnapshots,
  serializeBlocks
} from './sync.js'
import { extractLilodataBlock, parseLilodataText, updateLilodataText } from '../lilodata.js'
import { generateId, seedClock } from '../id.js'
import { spaceIdToDefaultSlug } from './indexNavigation.js'
import { resolveSyncStoragePath } from './syncStoragePath.js'
import {
  SYNC_SNAPSHOT_READ_ATTEMPTS,
  SYNC_SHELF_HYDRATION_AGE_STEP_MS,
  SYNC_SHELF_HYDRATION_BYTE_LIMIT,
  SYNC_SHELF_HYDRATION_ITEM_LIMIT,
  SYNC_SHELF_HYDRATION_RETRY_BASE_MS,
  SYNC_SHELF_HYDRATION_RETRY_MAX_MS
} from '../constants.js'
import type { Block, SpaceEntry } from '../types.js'
import { deriveSpaceIded } from './spaceIdentity.js'
import type {
  LocalShelfSpace,
  LocalSpaceMeta,
  SyncAcknowledgement,
  LocalVolumeAuthority,
  PayloadState,
  RemoteSpaceMetaInput,
  ReplaceLocalBlobFromRemoteInput,
  ReplaceLocalBlobFromRemoteResult,
  ShelfOwnerLane,
  WriteMergedLocalSpaceInput
} from './databaseMemory.js'
import { isTerminalPayloadFailure } from './databaseMemory.js'
import { decodeSpacePayload } from './spaceCodec.js'

export type SyncEngineState = {
  status: SyncStatus
  progress: number
}

export type SyncShelfSaveInput = {
  expectedPath: string | null
  spaceId: string
  spaceSlug: string
  spaceEd: string
  payload: string
}

export type SyncShelfSaveResult = {
  syncAt?: string | null
  spaceSlug?: string | null
  spaceEd?: string | null
  deletedAt?: string | null
}

export type SyncSpacePayloadLoadOutcome =
  | { status: 'payload', payload: string, meta: RemoteSpaceMetaInput }
  | { status: 'deleted', meta: RemoteSpaceMetaInput }
  | { status: 'missing-payload', meta: RemoteSpaceMetaInput }
  | { status: 'absent' }
  | { status: 'retryable-failure' }

export type SyncShelfSaveOutcome =
  | (SyncShelfSaveResult & { ok?: true })
  | { ok: false, reason: 'slug-conflict' | 'pointer-conflict' | 'failed' }
  | { ok: false, reason: 'deleted', meta: RemoteSpaceMetaInput }

export type SyncLocalVolume = {
  listVolumeSpacesMeta(authority?: LocalVolumeAuthority): Promise<LocalSpaceMeta[]>
  loadVolumeShelfSpace(spaceId: string, authority?: LocalVolumeAuthority): Promise<LocalShelfSpace | undefined>
  upsertRemoteSpacesMetaForVolume(input: RemoteSpaceMetaInput[], now?: string, authority?: LocalVolumeAuthority): Promise<LocalSpaceMeta[]>
  replaceVolumeBlobFromRemote(input: ReplaceLocalBlobFromRemoteInput, authority?: LocalVolumeAuthority): Promise<ReplaceLocalBlobFromRemoteResult>
  writeMergedVolumeSpace(input: WriteMergedLocalSpaceInput, authority?: LocalVolumeAuthority): Promise<LocalSpaceMeta | undefined>
  markVolumeSpaceDeleted(spaceId: string, deletedAt: string, now?: string, authority?: LocalVolumeAuthority): Promise<LocalSpaceMeta | undefined>
  purgeDeletedVolumePayload(spaceId: string, now?: string, authority?: LocalVolumeAuthority): Promise<LocalSpaceMeta | undefined>
  setVolumePayloadState(spaceId: string, payloadState: PayloadState, opts?: { errorAt?: string | null, spaceEd?: string }, authority?: LocalVolumeAuthority): Promise<void>
  recordVolumeSyncSuccess(
    spaceId: string,
    syncAt: string | null,
    now?: string,
    canonical?: SyncAcknowledgement,
    authority?: LocalVolumeAuthority
  ): Promise<void>
  renameVolumeSpaceForConflict(
    ownerId: string,
    spaceId: string,
    spaceSlug: string,
    spaceEd: string,
    expectedPayload: string,
    authority?: LocalVolumeAuthority
  ): Promise<LocalSpaceMeta | undefined>
}

export type SyncRemoteShelf = {
  listSpaces(authority?: LocalVolumeAuthority): Promise<RemoteSpaceMetaInput[]>
  loadSpacePayload(
    spaceId: string,
    authority?: LocalVolumeAuthority
  ): Promise<SyncSpacePayloadLoadOutcome>
  saveSpacePayload(
    input: SyncShelfSaveInput,
    authority?: LocalVolumeAuthority
  ): Promise<SyncShelfSaveOutcome>
  deleteSpace(
    input: { spaceId: string, deletedAt: string },
    authority?: LocalVolumeAuthority
  ): Promise<SyncShelfSaveResult>
}

export type SyncActiveEditorBridge = {
  getActiveSpaceId(): string | undefined
  prepareActiveSpace?(spaceId: string, authority?: LocalVolumeAuthority): Promise<void>
  applyActiveBlocks(
    blocks: SpaceEntry[],
    metaBlock?: Block,
    authority?: LocalVolumeAuthority
  ): Promise<void> | void
}

export type SyncShelfReconcileAction = 'noop' | 'pulled' | 'pushed' | 'merged' | 'deleted' | 'blocked'

export type SyncShelfReconcileResult = {
  ok: boolean
  action: SyncShelfReconcileAction
  pending?: boolean
  reason?:
    | 'local_changed'
    | 'missing_bridge'
    | 'missing_local_meta'
    | 'missing_local_payload'
    | 'missing_remote_payload'
    | 'remote_load_retryable'
    | 'remote_replace_blocked'
    | 'remote_save_failed'
    | 'slug_conflict'
    | 'pointer_conflict'
    | 'remote_delete_failed'
    | 'delete_conflict'
    | 'invalid_payload'
    | 'storage_quota'
    | 'write_blocked'
    | 'owner_mismatch'
}

export type SyncShelfFeedInput = {
  spaceIds?: string[]
  hydrateMissing?: boolean
  reconcileDirtyTombstonesForOwnerId?: string
  volumeAuthority?: LocalVolumeAuthority
}

export type SyncShelfFeedResult = {
  ok: boolean
  refreshed: boolean
  reconciled: Array<SyncShelfReconcileResult & { spaceId: string }>
  reason?: 'missing_bridge'
}

export type SyncOwnerMirrorIntent = 'hydrate' | 'reconcile' | 'skip'

export type SyncOwnerMirrorItem = SyncShelfReconcileResult & {
  spaceId: string
  intent: SyncOwnerMirrorIntent
}

export type SyncOwnerMirrorInput = {
  ownerId: string
  hydrationItemLimit?: number
  hydrationByteLimit?: number
  now?: number
  volumeAuthority?: LocalVolumeAuthority
}

export type SyncShelfSpaceInput = {
  ownerId: string
  spaceId: string
  volumeAuthority?: LocalVolumeAuthority
}

export type SyncOwnerMirrorResult = {
  ok: boolean
  refreshed: boolean
  mirrored: SyncOwnerMirrorItem[]
  hydration: {
    attempted: number
    estimatedBytes: number
    remaining: number
    terminal: number
    blockedByDebt: boolean
    quotaBlocked: boolean
    retryAfterMs?: number
  }
  reason?: 'missing_bridge'
}

export type SyncShelfHydrationCandidate = {
  spaceId: string
  spaceEd: string
  sizeBytes: number
  lastOpenedAt: string | null
  knownSince: string | null
}

export type SyncEngineOptions = {
  debounceMs: number
  onSync?: () => Promise<boolean>
  onState?: (state: SyncEngineState) => void
  canSyncWrite?: () => boolean
  canSyncShelfWrite?: () => boolean
  getActiveOwnerId?: () => string | undefined
  getVolumeAuthority?: () => LocalVolumeAuthority
  getDefaultSpaceSlug?: (spaceId: string) => string
  getActiveSpaceId?: () => string | undefined
  setPendingSync?: (spaceId: string, pending: boolean) => void
  isPendingSync?: (spaceId: string) => boolean
  onBlockedSync?: () => void
  localVolume?: SyncLocalVolume
  remoteShelf?: SyncRemoteShelf
  activeEditor?: SyncActiveEditorBridge
}

export class SyncEngine {
  private syncingSpaceId?: string
  private syncingAuthority?: LocalVolumeAuthority
  private readonly manager: SyncManager
  private state: SyncEngineState = { status: 'idle', progress: 0 }
  private readonly payloadHydrationInFlight = new Map<string, Promise<SyncShelfReconcileResult>>()
  private readonly payloadHydrationRetry = new Map<string, {
    attempts: number
    nextAt: number
    spaceEd: string
  }>()

  constructor(private readonly opts: SyncEngineOptions) {
    this.manager = new SyncManager({
      debounceMs: opts.debounceMs,
      onSync: () => this.performGuardedSync(),
      onStatus: (status, progress) => this.handleManagerStatus(status, progress)
    })
  }

  markDirty(): boolean {
    if (!this.canWrite()) {
      this.opts.onBlockedSync?.()
      return false
    }
    this.setActivePending(true)
    this.manager.markDirty()
    return true
  }

  async syncNow(): Promise<boolean> {
    return this.manager.syncNow()
  }

  isDirty(): boolean {
    return this.manager.isDirty()
  }

  stop(): void {
    this.manager.stop()
  }

  getState(): SyncEngineState {
    return { ...this.state }
  }

  setState(status: SyncStatus, progress: number): void {
    this.updateState(status, progress)
  }

  hasPendingActiveSpace(): boolean {
    const spaceId = this.opts.getActiveSpaceId?.()
    if (!spaceId) return false
    return this.opts.isPendingSync?.(spaceId) === true
  }

  async feedShelf(input: SyncShelfFeedInput = {}): Promise<SyncShelfFeedResult> {
    const localVolume = this.opts.localVolume
    const remoteShelf = this.opts.remoteShelf
    if (!localVolume || !remoteShelf) {
      return { ok: false, refreshed: false, reconciled: [], reason: 'missing_bridge' }
    }
    const authority = this.resolveVolumeAuthority(input.volumeAuthority)

    const remoteSpaces = await remoteShelf.listSpaces(authority)
    await localVolume.upsertRemoteSpacesMetaForVolume(remoteSpaces, undefined, authority)

    const hydrateIds = new Set<string>()
    if (input.hydrateMissing) {
      input.spaceIds?.forEach(spaceId => hydrateIds.add(spaceId))
    }
    const reconcileIds = new Set<string>()
    if (input.reconcileDirtyTombstonesForOwnerId) {
      const localRows = await localVolume.listVolumeSpacesMeta(authority)
      localRows
        .filter(row => {
          return row.owner_id === input.reconcileDirtyTombstonesForOwnerId
            && Boolean(row.deleted_at)
            && row.dirty
        })
        .forEach(row => reconcileIds.add(row.space_id))
    }

    const reconciled: Array<SyncShelfReconcileResult & { spaceId: string }> = []
    for (const spaceId of hydrateIds) {
      const remoteMeta = remoteSpaces.find(space => space.space_id === spaceId)
      if (!remoteMeta?.owner_id) {
        reconciled.push({ spaceId, ok: false, action: 'blocked', reason: 'missing_local_meta' })
        continue
      }
      const result = await this.hydrateShelfSpace({
        ownerId: remoteMeta.owner_id,
        spaceId,
        volumeAuthority: authority
      })
      reconciled.push({ spaceId, ...result })
    }
    for (const spaceId of reconcileIds) {
      const ownerId = input.reconcileDirtyTombstonesForOwnerId
      if (!ownerId) continue
      const result = await this.reconcileShelfSpace({
        ownerId,
        spaceId,
        volumeAuthority: authority
      })
      reconciled.push({ spaceId, ...result })
    }

    return {
      ok: reconciled.every(result => result.ok),
      refreshed: true,
      reconciled
    }
  }

  async hydrateShelfSpace(input: SyncShelfSpaceInput): Promise<SyncShelfReconcileResult> {
    const authority = this.resolveVolumeAuthority(input.volumeAuthority)
    const key = toPayloadHydrationKey(input.ownerId, input.spaceId, authority)
    const existing = this.payloadHydrationInFlight.get(key)
    if (existing) return existing

    const operation = (async (): Promise<SyncShelfReconcileResult> => {
      let result: SyncShelfReconcileResult
      try {
        result = await this.hydrateShelfSpaceOnce(input, authority)
      } catch (error) {
        if (!isStorageQuotaExceededError(error)) throw error
        result = { ok: false, action: 'blocked', reason: 'storage_quota' }
      }
      await this.recordPayloadHydrationOutcome(
        key,
        input.ownerId,
        input.spaceId,
        authority,
        result
      )
      return result
    })()
    this.payloadHydrationInFlight.set(key, operation)
    try {
      return await operation
    } finally {
      if (this.payloadHydrationInFlight.get(key) === operation) {
        this.payloadHydrationInFlight.delete(key)
      }
    }
  }

  private async hydrateShelfSpaceOnce(
    input: SyncShelfSpaceInput,
    authority: LocalVolumeAuthority | undefined
  ): Promise<SyncShelfReconcileResult> {
    const localVolume = this.opts.localVolume
    const remoteShelf = this.opts.remoteShelf
    if (!localVolume || !remoteShelf) {
      return { ok: false, action: 'blocked', reason: 'missing_bridge' }
    }

    const lane = toSyncShelfLane(input.ownerId)
    const local = await this.loadVolumeSpaceInLane(lane, input.spaceId, authority)
    if (!local) return { ok: false, action: 'blocked', reason: 'missing_local_meta' }
    if (local.meta.deleted_at) return { ok: true, action: 'deleted' }
    if (local.blob) return { ok: true, action: 'noop' }
    const terminalReason = resolveTerminalPayloadFailureReason(local.meta)
    if (terminalReason) return { ok: false, action: 'blocked', reason: terminalReason }
    return this.hydrateLocalMissingBlob(lane, local, authority)
  }

  private async recordPayloadHydrationOutcome(
    key: string,
    ownerId: string,
    spaceId: string,
    authority: LocalVolumeAuthority | undefined,
    result: SyncShelfReconcileResult
  ): Promise<void> {
    if (result.reason !== 'remote_load_retryable') {
      this.payloadHydrationRetry.delete(key)
      return
    }
    let local: LocalShelfSpace | undefined
    try {
      local = await this.loadVolumeSpaceInLane(
        toSyncShelfLane(ownerId),
        spaceId,
        authority
      )
    } catch {
      this.payloadHydrationRetry.delete(key)
      return
    }
    if (!local) {
      this.payloadHydrationRetry.delete(key)
      return
    }
    const previous = this.payloadHydrationRetry.get(key)
    const attempts = previous?.spaceEd === local.meta.space_ed
      ? previous.attempts + 1
      : 1
    const delay = Math.min(
      SYNC_SHELF_HYDRATION_RETRY_BASE_MS * (2 ** (attempts - 1)),
      SYNC_SHELF_HYDRATION_RETRY_MAX_MS
    )
    this.payloadHydrationRetry.set(key, {
      attempts,
      nextAt: Date.now() + delay,
      spaceEd: local.meta.space_ed
    })
  }

  private resolvePayloadHydrationRetryAfter(
    ownerId: string,
    rows: LocalSpaceMeta[],
    authority: LocalVolumeAuthority | undefined,
    now: number
  ): number | undefined {
    let nextAt: number | undefined
    for (const row of rows) {
      const key = toPayloadHydrationKey(ownerId, row.space_id, authority)
      const retry = this.payloadHydrationRetry.get(key)
      if (retry && retry.spaceEd !== row.space_ed) {
        this.payloadHydrationRetry.delete(key)
        return undefined
      }
      if (!retry || retry.nextAt <= now) return undefined
      nextAt = nextAt === undefined ? retry.nextAt : Math.min(nextAt, retry.nextAt)
    }
    return nextAt === undefined ? undefined : Math.max(0, Math.ceil(nextAt - now))
  }

  async syncActiveShelfSpace(input: SyncShelfSpaceInput): Promise<SyncShelfReconcileResult> {
    return this.reconcileShelfSpace(input)
  }

  async recoverActiveShelfSpace(input: SyncShelfSpaceInput): Promise<SyncShelfReconcileResult> {
    return this.reconcileShelfSpace(input)
  }

  async pushDeletedShelfSpace(input: SyncShelfSpaceInput): Promise<SyncShelfReconcileResult> {
    return this.reconcileShelfSpace(input)
  }

  async mirrorOwnerShelf(input: SyncOwnerMirrorInput): Promise<SyncOwnerMirrorResult> {
    const localVolume = this.opts.localVolume
    const remoteShelf = this.opts.remoteShelf
    if (!localVolume || !remoteShelf) {
      return {
        ok: false,
        refreshed: false,
        mirrored: [],
        hydration: createEmptyOwnerMirrorHydration(),
        reason: 'missing_bridge'
      }
    }
    const authority = this.resolveVolumeAuthority(input.volumeAuthority)
    const now = input.now ?? Date.now()

    const remoteSpaces = await remoteShelf.listSpaces(authority)
    await this.upsertRemoteSpacesMetaForLane(input.ownerId, remoteSpaces, authority)
    const remoteById = new Map(remoteSpaces.map(space => [space.space_id, space]))
    const rows = await localVolume.listVolumeSpacesMeta(authority)
    const candidates = rows.filter(row => {
      if (row.owner_id !== input.ownerId) return false
      if (row.deleted_at && !row.dirty) return false
      return true
    })

    const mirroredBySpace = new Map<string, SyncOwnerMirrorItem>()
    const lane = toSyncShelfLane(input.ownerId)
    for (const row of candidates) {
      const local = await this.loadVolumeSpaceInLane(lane, row.space_id, authority)
      if (!local) {
        mirroredBySpace.set(row.space_id, {
          spaceId: row.space_id,
          intent: 'skip',
          ok: false,
          action: 'blocked',
          reason: 'missing_local_meta'
        })
        continue
      }
      const remoteMeta = remoteById.get(row.space_id)
      if (shouldMirrorReconcile(local, remoteMeta)) {
        if (local.meta.dirty && !this.canWriteShelf()) {
          mirroredBySpace.set(row.space_id, {
            spaceId: row.space_id,
            intent: 'reconcile',
            ok: false,
            action: 'blocked',
            reason: 'write_blocked'
          })
          continue
        }
        const result = await this.reconcileShelfSpace({
          ownerId: input.ownerId,
          spaceId: row.space_id,
          volumeAuthority: authority
        }, remoteSpaces)
        mirroredBySpace.set(row.space_id, {
          spaceId: row.space_id,
          intent: 'reconcile',
          ...result
        })
        continue
      }
      const terminalReason = resolveTerminalPayloadFailureReason(local.meta)
      mirroredBySpace.set(row.space_id, terminalReason
        ? {
            spaceId: row.space_id,
            intent: 'skip',
            ok: false,
            action: 'blocked',
            reason: terminalReason
          }
        : {
            spaceId: row.space_id,
            intent: 'skip',
            ok: true,
            action: 'noop'
          })
    }

    let ownerRows = (await localVolume.listVolumeSpacesMeta(authority)).filter(row => (
      row.owner_id === input.ownerId && !row.deleted_at
    ))
    let blockedByDebt = ownerRows.some(row => row.dirty)
    let quotaBlocked = [...mirroredBySpace.values()].some(item => item.reason === 'storage_quota')
    let attempted = 0
    let estimatedBytes = 0

    if (!blockedByDebt && !quotaBlocked) {
      const readyRows: LocalSpaceMeta[] = []
      for (const row of ownerRows) {
        if (!isRetryableKnownPayload(row)) continue
        const key = toPayloadHydrationKey(input.ownerId, row.space_id, authority)
        const retry = this.payloadHydrationRetry.get(key)
        if (retry && retry.spaceEd !== row.space_ed) {
          this.payloadHydrationRetry.delete(key)
        }
        const currentRetry = this.payloadHydrationRetry.get(key)
        if (currentRetry && currentRetry.nextAt > now) continue
        readyRows.push(row)
      }

      const readyById = new Map(readyRows.map(row => [row.space_id, row]))
      const selectedIds = selectSyncShelfHydrationBatch(
        readyRows.map(toSyncShelfHydrationCandidate),
        {
          now,
          itemLimit: input.hydrationItemLimit ?? SYNC_SHELF_HYDRATION_ITEM_LIMIT,
          byteLimit: input.hydrationByteLimit ?? SYNC_SHELF_HYDRATION_BYTE_LIMIT
        }
      )
      for (const spaceId of selectedIds) {
        const row = readyById.get(spaceId)
        if (!row) continue
        attempted += 1
        estimatedBytes += normalizeHydrationSize(row.size_bytes)
        const result = await this.hydrateShelfSpace({
          ownerId: input.ownerId,
          spaceId,
          volumeAuthority: authority
        })
        mirroredBySpace.set(spaceId, {
          spaceId,
          intent: 'hydrate',
          ...result
        })
        if (result.reason === 'storage_quota') {
          quotaBlocked = true
          break
        }
        if (result.reason === 'remote_load_retryable') break
      }
      ownerRows = (await localVolume.listVolumeSpacesMeta(authority)).filter(row => (
        row.owner_id === input.ownerId && !row.deleted_at
      ))
    }

    blockedByDebt = ownerRows.some(row => row.dirty)
    const remainingRows = ownerRows.filter(isRetryableKnownPayload)
    const terminal = ownerRows.filter(isTerminalPayloadFailure).length
    const statusNow = input.now ?? Date.now()
    const retryAfterMs = this.resolvePayloadHydrationRetryAfter(
      input.ownerId,
      remainingRows,
      authority,
      statusNow
    )
    const mirrored = [...mirroredBySpace.values()]
    return {
      ok: mirrored.every(item => item.ok) && terminal === 0 && !quotaBlocked,
      refreshed: true,
      mirrored,
      hydration: {
        attempted,
        estimatedBytes,
        remaining: remainingRows.length,
        terminal,
        blockedByDebt,
        quotaBlocked,
        ...(retryAfterMs !== undefined ? { retryAfterMs } : {})
      }
    }
  }

  async reconcileShelfSpace(input: SyncShelfSpaceInput, catalogue?: RemoteSpaceMetaInput[]): Promise<SyncShelfReconcileResult> {
    const authority = this.resolveVolumeAuthority(input.volumeAuthority)
    const key = toPayloadHydrationKey(input.ownerId, input.spaceId, authority)
    const previous = this.payloadHydrationInFlight.get(key)
    const operation = (async (): Promise<SyncShelfReconcileResult> => {
      if (previous) await previous.catch(() => undefined)
      try {
        await this.opts.activeEditor?.prepareActiveSpace?.(input.spaceId, authority)
        return await this.reconcileShelfSpaceOnce({ ...input, volumeAuthority: authority }, catalogue)
      } catch (error) {
        return { ok: false, action: 'blocked', reason: isStorageQuotaExceededError(error) ? 'storage_quota' : 'remote_load_retryable' }
      }
    })()
    this.payloadHydrationInFlight.set(key, operation)
    try {
      return await operation
    } finally {
      if (this.payloadHydrationInFlight.get(key) === operation) this.payloadHydrationInFlight.delete(key)
    }
  }

  private async reconcileShelfSpaceOnce(input: SyncShelfSpaceInput, catalogue?: RemoteSpaceMetaInput[]): Promise<SyncShelfReconcileResult> {
    const localVolume = this.opts.localVolume
    const remoteShelf = this.opts.remoteShelf
    if (!localVolume || !remoteShelf) {
      return { ok: false, action: 'blocked', reason: 'missing_bridge' }
    }

    const authority = this.resolveVolumeAuthority(input.volumeAuthority)
    const lane = toSyncShelfLane(input.ownerId)
    const initialLocal = await this.loadVolumeSpaceInLane(
      lane,
      input.spaceId,
      authority
    )
    const remoteSpaces = catalogue ?? await remoteShelf.listSpaces(authority)
    if (!catalogue) await this.upsertRemoteSpacesMetaForLane(input.ownerId, remoteSpaces, authority)
    return this.reconcileShelfSpaceWithRemoteSpaces(
      lane,
      input.spaceId,
      remoteSpaces,
      authority,
      initialLocal
    )
  }

  private async reconcileShelfSpaceWithRemoteSpaces(
    lane: ShelfOwnerLane,
    spaceId: string,
    remoteSpaces: RemoteSpaceMetaInput[],
    authority: LocalVolumeAuthority | undefined,
    initialLocal?: LocalShelfSpace
  ): Promise<SyncShelfReconcileResult> {
    const localVolume = this.opts.localVolume
    if (!localVolume) {
      return { ok: false, action: 'blocked', reason: 'missing_bridge' }
    }

    const local = initialLocal ?? await this.loadVolumeSpaceInLane(
      lane,
      spaceId,
      authority
    )
    const remoteMeta = remoteSpaces.find(space => space.space_id === spaceId && space.owner_id === lane.ownerId)

    if (!local) {
      if (!remoteMeta) return { ok: true, action: 'noop' }
      if (remoteMeta.deleted_at) return { ok: true, action: 'deleted' }
      return this.pullRemoteIntoLocal(lane, remoteMeta, authority)
    }

    if (local.meta.deleted_at) {
      if (local.meta.dirty) return this.pushLocalTombstoneToRemote(local, remoteMeta, authority)
      await this.purgeDeletedVolumePayloadInLane(lane, local.meta.space_id, authority)
      return { ok: true, action: 'deleted' }
    }
    if (remoteMeta?.deleted_at) return this.applyRemoteTombstone(local, remoteMeta, authority)
    if (local.meta.dirty) return this.prepareAndSaveLocal(lane, local.meta.space_id, authority)

    if (!local.blob && remoteMeta) {
      const terminalReason = resolveTerminalPayloadFailureReason(local.meta)
      if (terminalReason && !isRemoteNewer(remoteMeta.space_ed, local.meta.space_ed)) {
        return { ok: false, action: 'blocked', reason: terminalReason }
      }
      return this.pullRemoteIntoLocal(lane, remoteMeta, authority)
    }

    if (!remoteMeta || !isRemoteNewer(remoteMeta.space_ed, local.meta.space_ed)) {
      return { ok: true, action: 'noop' }
    }

    return this.pullRemoteIntoLocal(lane, remoteMeta, authority)
  }

  private async markTerminalPayloadFailure(
    lane: ShelfOwnerLane,
    spaceId: string,
    payloadState: 'known' | 'missing',
    spaceEd: string | null | undefined,
    authority: LocalVolumeAuthority | undefined
  ): Promise<void> {
    const localVolume = this.opts.localVolume
    if (!localVolume) return
    const shelf = await this.loadVolumeSpaceInLane(lane, spaceId, authority)
    if (!shelf) return
    const normalizedSpaceEd = typeof spaceEd === 'string' && spaceEd.trim()
      ? spaceEd
      : shelf.meta.space_ed
    await localVolume.setVolumePayloadState(
      spaceId,
      payloadState,
      { errorAt: new Date().toISOString(), spaceEd: normalizedSpaceEd },
      authority
    )
  }

  private async performGuardedSync(): Promise<boolean> {
    if (!this.canWrite()) {
      this.setActivePending(false)
      this.opts.onBlockedSync?.()
      return true
    }
    this.syncingSpaceId = this.opts.getActiveSpaceId?.()
    this.syncingAuthority = this.resolveVolumeAuthority(undefined)
    if (this.opts.onSync) return this.opts.onSync()
    const spaceId = this.opts.getActiveSpaceId?.()
    if (!spaceId) return true
    const ownerId = this.getActiveOwnerId()
    if (!ownerId) return true
    const result = await this.syncActiveShelfSpace({ ownerId, spaceId })
    if (result.pending) this.manager.markDirty()
    return result.ok
  }

  private async hydrateLocalMissingBlob(
    lane: ShelfOwnerLane,
    local: LocalShelfSpace,
    authority: LocalVolumeAuthority | undefined
  ): Promise<SyncShelfReconcileResult> {
    const localVolume = this.opts.localVolume
    const remoteShelf = this.opts.remoteShelf
    if (!localVolume || !remoteShelf) {
      return { ok: false, action: 'blocked', reason: 'missing_bridge' }
    }

    const outcome = await this.readRemoteSpace(
      local.meta.space_id,
      authority
    )
    if (outcome.status === 'invalid-payload') {
      await this.markTerminalPayloadFailure(lane, outcome.meta.space_id, 'known', outcome.meta.space_ed, authority)
      return { ok: false, action: 'blocked', reason: 'invalid_payload' }
    }
    if (outcome.status === 'deleted') {
      const latest = await this.loadVolumeSpaceInLane(lane, outcome.meta.space_id, authority)
      return latest ? this.applyRemoteTombstone(latest, outcome.meta, authority) : { ok: true, action: 'deleted' }
    }
    if (outcome.status === 'retryable-failure') {
      return { ok: false, action: 'blocked', reason: 'remote_load_retryable' }
    }
    if (outcome.status === 'absent' || outcome.status === 'missing-payload') {
      await this.markTerminalPayloadFailure(
        lane,
        local.meta.space_id,
        'missing',
        local.meta.space_ed,
        authority
      )
      return { ok: false, action: 'blocked', reason: 'missing_remote_payload' }
    }
    const payload = outcome.payload

    let result: ReplaceLocalBlobFromRemoteResult
    try {
      result = await this.replaceVolumeBlobFromRemoteInLane(lane, {
        remoteMeta: outcome.meta,
        payload
      }, authority)
    } catch (error) {
      if (isStorageQuotaExceededError(error)) {
        return { ok: false, action: 'blocked', reason: 'storage_quota' }
      }
      throw error
    }
    if (!result.ok) {
      if (result.reason === 'invalid_payload') {
        await this.markTerminalPayloadFailure(
          lane,
          local.meta.space_id,
          'known',
          local.meta.space_ed,
          authority
        )
        return { ok: false, action: 'blocked', reason: 'invalid_payload' }
      }
      return {
        ok: false,
        action: 'blocked',
        reason: result.reason === 'owner_mismatch' ? 'owner_mismatch' : 'remote_replace_blocked'
      }
    }
    const applied = await this.applyActivePayloadIfNeeded(
      local.meta.space_id,
      payload,
      authority
    )
    if (!applied) {
      return { ok: false, action: 'blocked', reason: 'invalid_payload' }
    }
    return { ok: true, action: 'pulled' }
  }

  private async pullRemoteIntoLocal(
    lane: ShelfOwnerLane,
    remoteMeta: RemoteSpaceMetaInput,
    authority: LocalVolumeAuthority | undefined
  ): Promise<SyncShelfReconcileResult> {
    const localVolume = this.opts.localVolume
    const remoteShelf = this.opts.remoteShelf
    if (!localVolume || !remoteShelf) {
      return { ok: false, action: 'blocked', reason: 'missing_bridge' }
    }

    const outcome = await this.readRemoteSpace(
      remoteMeta.space_id,
      authority
    )
    if (outcome.status === 'invalid-payload') {
      await this.markTerminalPayloadFailure(lane, outcome.meta.space_id, 'known', outcome.meta.space_ed, authority)
      return { ok: false, action: 'blocked', reason: 'invalid_payload' }
    }
    if (outcome.status === 'deleted') {
      const latest = await this.loadVolumeSpaceInLane(lane, outcome.meta.space_id, authority)
      return latest ? this.applyRemoteTombstone(latest, outcome.meta, authority) : { ok: true, action: 'deleted' }
    }
    if (outcome.status === 'retryable-failure') {
      return { ok: false, action: 'blocked', reason: 'remote_load_retryable' }
    }
    if (outcome.status === 'absent' || outcome.status === 'missing-payload') {
      await this.markTerminalPayloadFailure(
        lane,
        remoteMeta.space_id,
        'missing',
        remoteMeta.space_ed,
        authority
      )
      return { ok: false, action: 'blocked', reason: 'missing_remote_payload' }
    }
    const payload = outcome.payload
    let result: ReplaceLocalBlobFromRemoteResult
    try {
      result = await this.replaceVolumeBlobFromRemoteInLane(
        lane,
        { remoteMeta: outcome.meta, payload },
        authority
      )
    } catch (error) {
      if (isStorageQuotaExceededError(error)) {
        return { ok: false, action: 'blocked', reason: 'storage_quota' }
      }
      throw error
    }
    if (!result.ok) {
      if (result.reason === 'invalid_payload') {
        await this.markTerminalPayloadFailure(
          lane,
          remoteMeta.space_id,
          'known',
          remoteMeta.space_ed,
          authority
        )
        return { ok: false, action: 'blocked', reason: 'invalid_payload' }
      }
      return {
        ok: false,
        action: 'blocked',
        reason: result.reason === 'owner_mismatch' ? 'owner_mismatch' : 'remote_replace_blocked'
      }
    }
    const applied = await this.applyActivePayloadIfNeeded(
      remoteMeta.space_id,
      payload,
      authority
    )
    if (!applied) {
      return { ok: false, action: 'blocked', reason: 'invalid_payload' }
    }
    return { ok: true, action: 'pulled' }
  }

  private async pushLocalTombstoneToRemote(
    local: LocalShelfSpace,
    remoteMeta: RemoteSpaceMetaInput | undefined,
    authority: LocalVolumeAuthority | undefined
  ): Promise<SyncShelfReconcileResult> {
    const localVolume = this.opts.localVolume
    const remoteShelf = this.opts.remoteShelf
    const deletedAt = local.meta.deleted_at
    if (!localVolume || !remoteShelf || !deletedAt) {
      return { ok: false, action: 'blocked', reason: 'missing_bridge' }
    }
    if (!local.meta.owner_id) {
      return { ok: false, action: 'blocked', reason: 'owner_mismatch' }
    }
    const lane = toSyncShelfLane(local.meta.owner_id)

    if (remoteMeta?.deleted_at && remoteMeta.deleted_at.localeCompare(deletedAt) > 0) {
      await this.recordVolumeSyncSuccessInLane(
        lane,
        local.meta.space_id,
        remoteMeta.sync_at ?? null,
        undefined,
        { spaceEd: remoteMeta.space_ed ?? remoteMeta.deleted_at },
        authority
      )
      await this.purgeDeletedVolumePayloadInLane(lane, local.meta.space_id, authority)
      return { ok: true, action: 'deleted' }
    }

    try {
      const result = await remoteShelf.deleteSpace({
        spaceId: local.meta.space_id,
        deletedAt
      }, authority)
      await this.recordVolumeSyncSuccessInLane(
        lane,
        local.meta.space_id,
        result.syncAt ?? null,
        undefined,
        { spaceEd: result.spaceEd ?? result.deletedAt ?? deletedAt, sent: { spaceEd: local.meta.space_ed } },
        authority
      )
      await this.purgeDeletedVolumePayloadInLane(lane, local.meta.space_id, authority)
      return { ok: true, action: 'deleted' }
    } catch {
      return { ok: false, action: 'blocked', reason: 'remote_delete_failed' }
    }
  }

  private async applyRemoteTombstone(
    local: LocalShelfSpace,
    remoteMeta: RemoteSpaceMetaInput,
    authority: LocalVolumeAuthority | undefined
  ): Promise<SyncShelfReconcileResult> {
    const localVolume = this.opts.localVolume
    const deletedAt = remoteMeta.deleted_at
    if (!localVolume || !deletedAt) {
      return { ok: false, action: 'blocked', reason: 'missing_bridge' }
    }

    if (local.meta.dirty && local.meta.space_ed.localeCompare(deletedAt) > 0) {
      return { ok: false, action: 'blocked', reason: 'delete_conflict' }
    }

    const lane = toSyncShelfLane(local.meta.owner_id)
    await this.markVolumeSpaceDeletedInLane(lane, local.meta.space_id, deletedAt, authority)
    await this.recordVolumeSyncSuccessInLane(
      lane,
      local.meta.space_id,
      remoteMeta.sync_at ?? null,
      undefined,
      { spaceEd: remoteMeta.space_ed ?? deletedAt },
      authority
    )
    await this.purgeDeletedVolumePayloadInLane(lane, local.meta.space_id, authority)
    return { ok: true, action: 'deleted' }
  }

  private async readRemoteSpace(
    spaceId: string,
    authority?: LocalVolumeAuthority
  ): Promise<SyncSpacePayloadLoadOutcome | { status: 'invalid-payload', meta: RemoteSpaceMetaInput }> {
    const remote = this.opts.remoteShelf!
    let previous: string | undefined
    let observedMeta: RemoteSpaceMetaInput | undefined
    for (let attempt = 0; attempt < SYNC_SNAPSHOT_READ_ATTEMPTS; attempt += 1) {
      const outcome = await remote.loadSpacePayload(spaceId, authority)
      if (outcome.status === 'absent' && observedMeta) return { status: 'missing-payload', meta: observedMeta }
      if ('meta' in outcome) observedMeta = outcome.meta
      if (outcome.status !== 'payload' && outcome.status !== 'missing-payload') return outcome
      let valid = false
      if (outcome.status === 'payload') {
        try {
          valid = outcome.meta.space_id === spaceId
            && (!authority || outcome.meta.owner_id === authority.owner_id)
            && isPayloadIdentityAligned(await decodeSpacePayload(outcome.payload), outcome.meta)
        } catch { /* Retry a potentially interrupted cloud write before classifying damage. */ }
      }
      if (valid) return outcome
      const evidence = JSON.stringify(outcome)
      if (previous === evidence) {
        return outcome.status === 'missing-payload' ? outcome : { status: 'invalid-payload', meta: outcome.meta }
      }
      previous = evidence
    }
    return { status: 'retryable-failure' }
  }

  private async prepareAndSaveLocal(
    lane: ShelfOwnerLane,
    spaceId: string,
    authority?: LocalVolumeAuthority,
    allowSlugFallback = true,
    allowNameRepair = true
  ): Promise<SyncShelfReconcileResult> {
    if (!this.canWriteShelf()) return { ok: false, action: 'blocked', reason: 'write_blocked' }
    const remote = await this.readRemoteSpace(spaceId, authority)
    if (remote.status === 'retryable-failure') return { ok: false, action: 'blocked', reason: 'remote_load_retryable' }
    if (remote.status === 'invalid-payload') return { ok: false, action: 'blocked', reason: 'invalid_payload' }
    await this.opts.activeEditor?.prepareActiveSpace?.(spaceId, authority)
    const local = await this.loadVolumeSpaceInLane(lane, spaceId, authority)
    if (!local) return { ok: false, action: 'blocked', reason: 'missing_local_meta' }
    if (local.meta.deleted_at) return this.pushLocalTombstoneToRemote(local, 'meta' in remote ? remote.meta : undefined, authority)
    if (remote.status === 'deleted') return this.applyRemoteTombstone(local, remote.meta, authority)
    if (remote.status === 'missing-payload' || (remote.status === 'absent' && (local.meta.sync_at || local.meta.last_sync_ok_at))) {
      return { ok: false, action: 'blocked', reason: 'missing_remote_payload' }
    }
    const localBlocks = await this.loadLocalBlocksForMerge(local)
    if (!localBlocks || !local.blob) return { ok: false, action: 'blocked', reason: 'missing_local_payload' }
    const remoteEntries = remote.status === 'payload' ? await decodeSpacePayload(remote.payload) : undefined
    const merged = mergeSpaceSnapshots(localBlocks, remoteEntries ?? localBlocks)
    const changed = serializeBlocks(merged.mergedAll) !== serializeBlocks(localBlocks)
    if (changed) {
      const written = await this.opts.localVolume!.writeMergedVolumeSpace({
        expectedPayload: local.blob.payload,
        blocksWithMeta: merged.mergedAll,
        ownerId: lane.ownerId,
        dirty: true
      }, authority)
      if (!written) return { ok: false, action: 'blocked', reason: 'local_changed' }
      await this.applyActiveBlocksIfNeeded(spaceId, merged.mergedAll, authority)
    }
    const candidate = await this.loadVolumeSpaceInLane(lane, spaceId, authority)
    if (!candidate?.blob || candidate.meta.deleted_at) return { ok: false, action: 'blocked', reason: 'local_changed' }
    const entries = await this.loadLocalBlocksForMerge(candidate)
    if (!entries || !isPayloadIdentityAligned(entries, candidate.meta)) {
      return { ok: false, action: 'blocked', reason: 'invalid_payload' }
    }
    // A retry must merge again if another local writer replaced the prepared candidate.
    if (remoteEntries) {
      const combined = mergeBlocks(entries, remoteEntries)
      const current = extractLilodataBlock(entries)
      if (serializeBlocks(combined.blocks) !== serializeBlocks(current.blocks)
        || combined.metaBlock?.text !== current.metaBlock?.text) {
        return { ok: false, action: 'blocked', reason: 'local_changed' }
      }
    }
    const { metaBlock } = extractLilodataBlock(entries)
    const slug = candidate.meta.space_slug?.trim()
    const documentSlug = metaBlock && parseLilodataText(metaBlock.text).slug?.trim()
    if (!slug || !documentSlug) {
      if (!allowNameRepair) return { ok: false, action: 'blocked', reason: 'local_changed' }
      if (!metaBlock) return { ok: false, action: 'blocked', reason: 'invalid_payload' }
      const resolvedSlug = documentSlug || slug || this.opts.getDefaultSpaceSlug?.(spaceId) || spaceIdToDefaultSlug(spaceId)
      seedClock(entries)
      const named = entries.map(entry => entry === metaBlock ? {
        ...metaBlock,
        text: updateLilodataText(metaBlock.text, { slug: resolvedSlug }),
        ided: [metaBlock.ided[0], generateId()] as [string, string]
      } : entry)
      // Name the document locally before uploading it. The payload guard protects
      // a rename or writing that arrived while this snapshot was being prepared.
      const written = await this.opts.localVolume!.writeMergedVolumeSpace({
        expectedPayload: candidate.blob.payload, blocksWithMeta: named,
        ownerId: lane.ownerId, dirty: true
      }, authority)
      if (!written) return { ok: false, action: 'blocked', reason: 'local_changed' }
      await this.applyActiveBlocksIfNeeded(spaceId, named, authority)
      return this.prepareAndSaveLocal(lane, spaceId, authority, allowSlugFallback, false)
    }
    const input: SyncShelfSaveInput = {
      expectedPath: remote.status === 'payload'
        ? resolveSyncStoragePath(lane.ownerId!, spaceId, remote.meta.path)
        : null,
      spaceId,
      spaceSlug: slug,
      spaceEd: candidate.meta.space_ed,
      payload: JSON.stringify(entries)
    }
    const sameAsCloud = remoteEntries !== undefined
      && serializeBlocks(entries) === serializeBlocks(remoteEntries)
    let outcome: SyncShelfSaveOutcome
    try {
      outcome = sameAsCloud && remote.status === 'payload'
        ? { syncAt: remote.meta.sync_at, spaceSlug: remote.meta.space_slug, spaceEd: remote.meta.space_ed }
        : await this.opts.remoteShelf!.saveSpacePayload(input, authority)
    } catch {
      return { ok: false, action: 'blocked', reason: 'remote_save_failed' }
    }
    if ('ok' in outcome && outcome.ok === false) {
      if (outcome.reason === 'pointer-conflict') return { ok: false, action: 'blocked', reason: 'pointer_conflict' }
      if (outcome.reason === 'deleted') {
        const latest = await this.loadVolumeSpaceInLane(lane, spaceId, authority)
        return latest ? this.applyRemoteTombstone(latest, outcome.meta, authority)
          : { ok: false, action: 'blocked', reason: 'missing_local_meta' }
      }
      if (outcome.reason !== 'slug-conflict') return { ok: false, action: 'blocked', reason: 'remote_save_failed' }
      const fallback = this.opts.getDefaultSpaceSlug?.(spaceId) || spaceIdToDefaultSlug(spaceId)
      if (!allowSlugFallback || candidate.meta.space_slug === fallback) return { ok: false, action: 'blocked', reason: 'slug_conflict' }
      seedClock(entries)
      const renamed = await this.opts.localVolume!.renameVolumeSpaceForConflict(
        lane.ownerId!, spaceId, fallback, generateId(), candidate.blob.payload, authority
      )
      if (!renamed) return { ok: false, action: 'blocked', reason: 'local_changed' }
      return this.prepareAndSaveLocal(lane, spaceId, authority, false)
    }
    await this.recordVolumeSyncSuccessInLane(lane, spaceId, outcome.syncAt ?? null, undefined, {
      spaceSlug: outcome.spaceSlug,
      spaceEd: outcome.spaceEd,
      sent: { spaceEd: candidate.meta.space_ed, payload: candidate.blob.payload }
    }, authority)
    const latest = await this.loadVolumeSpaceInLane(lane, spaceId, authority)
    return { ok: true, action: changed ? 'merged' : sameAsCloud ? 'noop' : 'pushed', ...(latest?.meta.dirty ? { pending: true } : {}) }
  }

  private async loadLocalBlocksForMerge(local: LocalShelfSpace): Promise<SpaceEntry[] | undefined> {
    if (!local.blob) return undefined
    try {
      return await decodeSpacePayload(local.blob.payload)
    } catch {
      return undefined
    }
  }

  private async applyActivePayloadIfNeeded(
    spaceId: string,
    payload: string,
    authority: LocalVolumeAuthority | undefined
  ): Promise<boolean> {
    if (!this.isActiveSpace(spaceId)) return true
    try {
      const blocksWithMeta = await decodeSpacePayload(payload)
      await this.applyActiveBlocksIfNeeded(spaceId, blocksWithMeta, authority)
      return true
    } catch {
      return false
    }
  }

  private async applyActiveBlocksIfNeeded(
    spaceId: string,
    blocksWithMeta: SpaceEntry[],
    authority: LocalVolumeAuthority | undefined
  ): Promise<void> {
    if (!this.isActiveSpace(spaceId)) return
    const { blocks, metaBlock } = extractLilodataBlock(blocksWithMeta)
    await this.opts.activeEditor?.applyActiveBlocks(blocks, metaBlock, authority)
  }

  private isActiveSpace(spaceId: string): boolean {
    return this.opts.activeEditor?.getActiveSpaceId() === spaceId
  }

  private handleManagerStatus(status: SyncStatus, progress: number): void {
    if (status === 'success' && this.syncingSpaceId
      && JSON.stringify(this.syncingAuthority) === JSON.stringify(this.resolveVolumeAuthority(undefined))) {
      this.opts.setPendingSync?.(this.syncingSpaceId, false)
    }
    this.updateState(status, progress)
  }

  private updateState(status: SyncStatus, progress: number): void {
    this.state = { status, progress }
    this.opts.onState?.({ ...this.state })
  }

  private setActivePending(pending: boolean): void {
    const spaceId = this.opts.getActiveSpaceId?.()
    if (!spaceId) return
    this.opts.setPendingSync?.(spaceId, pending)
  }

  private canWrite(): boolean {
    return this.opts.canSyncWrite?.() ?? true
  }

  private canWriteShelf(): boolean {
    return this.opts.canSyncShelfWrite?.() ?? this.canWrite()
  }

  private getActiveOwnerId(): string | undefined {
    return this.opts.getActiveOwnerId?.()
  }

  private resolveVolumeAuthority(
    provided: LocalVolumeAuthority | undefined
  ): LocalVolumeAuthority | undefined {
    const authority = provided !== undefined
      ? provided
      : this.opts.getVolumeAuthority?.()
    return authority ? { ...authority } : authority
  }

  private async loadVolumeSpaceInLane(
    lane: ShelfOwnerLane,
    spaceId: string,
    authority: LocalVolumeAuthority | undefined
  ): Promise<LocalShelfSpace | undefined> {
    const localVolume = this.opts.localVolume
    if (!localVolume) return undefined
    if (authority && authority.owner_id !== lane.ownerId) return undefined
    const shelf = await localVolume.loadVolumeShelfSpace(spaceId, authority)
    if (!shelf || shelf.meta.owner_id !== lane.ownerId) return undefined
    return shelf
  }

  private async upsertRemoteSpacesMetaForLane(
    ownerId: string,
    remoteSpaces: RemoteSpaceMetaInput[],
    authority: LocalVolumeAuthority | undefined
  ): Promise<void> {
    const localVolume = this.opts.localVolume
    if (!localVolume) return
    if (authority && authority.owner_id !== ownerId) return
    await localVolume.upsertRemoteSpacesMetaForVolume(
      remoteSpaces.filter(space => space.owner_id === ownerId),
      undefined,
      authority
    )
  }

  private async replaceVolumeBlobFromRemoteInLane(
    lane: ShelfOwnerLane,
    input: ReplaceLocalBlobFromRemoteInput,
    authority: LocalVolumeAuthority | undefined
  ): Promise<ReplaceLocalBlobFromRemoteResult> {
    const localVolume = this.opts.localVolume
    if (!localVolume) return { ok: false, reason: 'owner_mismatch' }
    if (input.remoteMeta.owner_id !== lane.ownerId) return { ok: false, reason: 'owner_mismatch' }
    if (authority && authority.owner_id !== lane.ownerId) {
      return { ok: false, reason: 'owner_mismatch' }
    }
    return localVolume.replaceVolumeBlobFromRemote(input, authority)
  }

  private async markVolumeSpaceDeletedInLane(
    lane: ShelfOwnerLane,
    spaceId: string,
    deletedAt: string,
    authority: LocalVolumeAuthority | undefined
  ): Promise<LocalSpaceMeta | undefined> {
    const localVolume = this.opts.localVolume
    if (!localVolume) return undefined
    if (authority && authority.owner_id !== lane.ownerId) return undefined
    return localVolume.markVolumeSpaceDeleted(spaceId, deletedAt, undefined, authority)
  }

  private async purgeDeletedVolumePayloadInLane(
    lane: ShelfOwnerLane,
    spaceId: string,
    authority: LocalVolumeAuthority | undefined
  ): Promise<LocalSpaceMeta | undefined> {
    const localVolume = this.opts.localVolume
    if (!localVolume) return undefined
    if (authority && authority.owner_id !== lane.ownerId) return undefined
    return localVolume.purgeDeletedVolumePayload(spaceId, undefined, authority)
  }

  private async recordVolumeSyncSuccessInLane(
    lane: ShelfOwnerLane,
    spaceId: string,
    syncAt: string | null,
    now?: string,
    canonical?: SyncAcknowledgement,
    authority?: LocalVolumeAuthority
  ): Promise<void> {
    const localVolume = this.opts.localVolume
    if (!localVolume) return
    if (authority && authority.owner_id !== lane.ownerId) return
    await localVolume.recordVolumeSyncSuccess(
      spaceId,
      syncAt,
      now,
      canonical,
      authority
    )
  }
}

export function selectSyncShelfHydrationBatch(
  candidates: SyncShelfHydrationCandidate[],
  input: { now: number, itemLimit: number, byteLimit: number }
): string[] {
  const itemLimit = Math.max(1, Math.floor(input.itemLimit))
  const byteLimit = Math.max(1, Math.floor(input.byteLimit))
  const ranked = [...candidates].sort((a, b) => {
    const ageDifference = hydrationAgeTier(b, input.now) - hydrationAgeTier(a, input.now)
    if (ageDifference !== 0) return ageDifference
    const openedDifference = parseHydrationTimestamp(b.lastOpenedAt) - parseHydrationTimestamp(a.lastOpenedAt)
    if (openedDifference !== 0) return openedDifference
    const sizeDifference = normalizeHydrationSize(a.sizeBytes) - normalizeHydrationSize(b.sizeBytes)
    if (sizeDifference !== 0) return sizeDifference
    const editDifference = b.spaceEd.localeCompare(a.spaceEd)
    return editDifference !== 0 ? editDifference : a.spaceId.localeCompare(b.spaceId)
  })

  const selected: string[] = []
  let estimatedBytes = 0
  for (const candidate of ranked) {
    if (selected.length >= itemLimit) break
    const sizeBytes = normalizeHydrationSize(candidate.sizeBytes)
    if (selected.length === 0) {
      selected.push(candidate.spaceId)
      estimatedBytes = sizeBytes
      continue
    }
    if (estimatedBytes + sizeBytes > byteLimit) continue
    selected.push(candidate.spaceId)
    estimatedBytes += sizeBytes
  }
  return selected
}

export function isStorageQuotaExceededError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false
  const record = error as { name?: unknown, code?: unknown }
  return record.name === 'QuotaExceededError'
    || record.name === 'NS_ERROR_DOM_QUOTA_REACHED'
    || record.code === 22
    || record.code === 1014
}

function createEmptyOwnerMirrorHydration(): SyncOwnerMirrorResult['hydration'] {
  return {
    attempted: 0,
    estimatedBytes: 0,
    remaining: 0,
    terminal: 0,
    blockedByDebt: false,
    quotaBlocked: false
  }
}

function isRetryableKnownPayload(row: LocalSpaceMeta): boolean {
  return !row.deleted_at
    && !row.dirty
    && row.payload_state === 'known'
    && !isTerminalPayloadFailure(row)
}

function toSyncShelfHydrationCandidate(row: LocalSpaceMeta): SyncShelfHydrationCandidate {
  return {
    spaceId: row.space_id,
    spaceEd: row.space_ed,
    sizeBytes: row.size_bytes,
    lastOpenedAt: row.last_opened_at,
    knownSince: row.last_sync_ok_at
  }
}

function hydrationAgeTier(candidate: SyncShelfHydrationCandidate, now: number): number {
  const knownSince = parseHydrationTimestamp(candidate.knownSince)
  if (!knownSince) return 0
  return Math.floor(Math.max(0, now - knownSince) / SYNC_SHELF_HYDRATION_AGE_STEP_MS)
}

function parseHydrationTimestamp(value: string | null): number {
  if (!value) return 0
  const parsed = Date.parse(value)
  return Number.isFinite(parsed) ? parsed : 0
}

function normalizeHydrationSize(value: number): number {
  return Number.isFinite(value) && value > 0 ? Math.floor(value) : 0
}

function toPayloadHydrationKey(
  ownerId: string,
  spaceId: string,
  authority: LocalVolumeAuthority | undefined
): string {
  return `${ownerId}\u0000${authority?.mount_id ?? 'unmounted'}\u0000${spaceId}`
}

function toSyncShelfLane(ownerId: string | null | undefined): ShelfOwnerLane {
  return { ownerId: ownerId ?? '' }
}

function isRemoteNewer(remoteEd: string | null | undefined, localEd: string): boolean {
  const normalizedRemote = typeof remoteEd === 'string' ? remoteEd.trim() : ''
  if (!normalizedRemote) return false
  return normalizedRemote.localeCompare(localEd) > 0
}

function shouldMirrorReconcile(
  local: LocalShelfSpace,
  remoteMeta?: RemoteSpaceMetaInput
): boolean {
  if (local.meta.deleted_at) return local.meta.dirty
  if (remoteMeta?.deleted_at) return true
  if (local.meta.dirty) return true
  if (!local.blob) return false
  return isRemoteNewer(remoteMeta?.space_ed, local.meta.space_ed)
}

function resolveTerminalPayloadFailureReason(
  meta: LocalSpaceMeta
): 'missing_remote_payload' | 'invalid_payload' | undefined {
  if (!isTerminalPayloadFailure(meta)) return undefined
  return meta.payload_state === 'missing'
    ? 'missing_remote_payload'
    : 'invalid_payload'
}

function isPayloadIdentityAligned(blocksWithMeta: SpaceEntry[], remoteMeta: RemoteSpaceMetaInput): boolean {
  const { blocks, metaBlock } = extractLilodataBlock(blocksWithMeta)
  const identity = metaBlock
    ? { spaceId: metaBlock.ided[0], spaceEd: metaBlock.ided[1] }
    : blocks.length
      ? (() => {
          const derived = deriveSpaceIded(blocks)
          return { spaceId: derived.createdId, spaceEd: derived.editedId }
        })()
      : undefined
  if (!identity) return true
  if (identity.spaceId && identity.spaceId !== remoteMeta.space_id) return false
  const remoteSpaceEd = remoteMeta.space_ed?.trim()
  if (remoteSpaceEd && identity.spaceEd && identity.spaceEd !== remoteSpaceEd) return false
  return true
}
